import React from "react";
import {
  Globe,
  Zap,
  Layers,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  Monitor,
  MousePointerClick,
  Rocket,
  Search,
  Layout,
  Gauge,
  Smartphone,
  Building2,
  Users2,
  Code
} from "lucide-react";
import { motion } from "framer-motion";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Navbar from "../Components/Navbar";
import Call from "../Components/Call";
import Footer from "../Components/Footer";
import ChatbotWidget from "./ChatComponent";
import TrustStatement from "../Components/TrustStatement";
import "../Styling/WS.css";

gsap.registerPlugin(ScrollTrigger);

const containerVariants = {
  hidden: { opacity: 1 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0,
      delayChildren: 0
    }
  }
};

const itemVariants = {
  hidden: { opacity: 1, y: 0 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0 }
  }
};

export default function WS() {
  const [currentFeature, setCurrentFeature] = React.useState(0);
  const totalFeatures = 3;
  const mainRef = React.useRef(null);

  React.useEffect(() => {
    const interval = setInterval(() => {
      setCurrentFeature((prev) => (prev + 1) % totalFeatures);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  React.useEffect(() => {
    const ctx = gsap.context((self) => {
      // Floating Animation for Cards
      gsap.to(".main-card", {
        y: -15,
        duration: 2.5,
        repeat: -1,
        yoyo: true,
        ease: "power1.inOut"
      });

      gsap.to(".card-1", {
        y: 12,
        x: 8,
        duration: 3,
        repeat: -1,
        yoyo: true,
        ease: "power1.inOut"
      });

      gsap.to(".card-2", {
        y: -12,
        x: -8,
        duration: 3.5,
        repeat: -1,
        yoyo: true,
        ease: "power1.inOut"
      });

      // Background Blobs
      gsap.to(".WS-blob", {
        y: "30%",
        rotation: 10,
        duration: 8,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
        stagger: {
          each: 2,
          from: "random"
        }
      });

      // Mesh Overlay Rotation
      gsap.to(".WS-mesh-overlay", {
        rotation: 360,
        duration: 120,
        repeat: -1,
        ease: "linear"
      });

      ScrollTrigger.refresh();
    }, mainRef);

    const refreshTimer = setTimeout(() => {
      ScrollTrigger.refresh();
    }, 500);

    return () => {
      ctx.revert();
      clearTimeout(refreshTimer);
    };
  }, []);

  const nextFeature = () => setCurrentFeature((prev) => (prev + 1) % totalFeatures);
  const prevFeature = () => setCurrentFeature((prev) => (prev === 0 ? totalFeatures - 1 : prev - 1));
  const goToFeature = (index) => setCurrentFeature(index);

  return (
    <div className="WS-page-wrapper" ref={mainRef}>
      <Navbar />

      {/* HERO SECTION (CINEMATIC) */}
      <section className="WS-header-section">
        {/* Background Effects */}
        <div className="WS-mesh-container">
          <div className="WS-mesh-overlay"></div>
        </div>
        <div className="WS-blob blob-azure"></div>
        <div className="WS-blob blob-blue"></div>
        <div className="WS-blob blob-indigo"></div>

        <div className="container">
          <div className="WS-hero-layout">
            <motion.div
              className="WS-hero-content"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              <motion.div className="WS-header-badge" variants={itemVariants}>
                <Globe size={16} />
                <span>Revenue Infrastructure</span>
              </motion.div>

              <motion.h1 className="WS-header-title" variants={itemVariants}>
                Web Experiences Engineered to <span className="azure-gradient">Convert & Scale</span>
              </motion.h1>

              <motion.p className="WS-header-desc" variants={itemVariants}>
                We design and build high‑performance websites and landing systems focused on conversion, speed, and measurable outcomes. These are not design exercises, but revenue‑driven systems.
              </motion.p>

              <motion.div className="WS-hero-ctas" variants={itemVariants}>
                <a
                  href="https://studio.digitaldreamworksagency.com/access"
                  className="WS-btn-hero"
                  onClick={() => {
                    if (window.fbq) {
                      window.fbq('trackCustom', 'RequestDemoOpened', {
                        source: 'ws_hero',
                        page_title: 'Web Systems (WS)'
                      });
                    }
                  }}
                >
                  Request Demo Access <ArrowRight size={18} />
                </a>
                <div className="WS-trust">
                  <Rocket size={16} color="#0ea5e9" />
                  <span>Sub-second Load</span>
                </div>
              </motion.div>
            </motion.div>

            <div className="WS-hero-visual">
              <div className="visual-card main-card">
                <Monitor size={64} strokeWidth={1.5} />
                <span>Conversion UX</span>
              </div>
              <div className="visual-card secondary-card card-1">
                <Code size={32} color="#0ea5e9" />
                <span>Clean Code</span>
              </div>
              <div className="visual-card secondary-card card-2">
                <Gauge size={32} color="#3b82f6" />
                <span>Performance</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SLIDER SECTION */}
      <section className="WS-slider-section">
        <div className="container">
          <div className="WS-slider-header">
            <h2 className="WS-slider-title">Visual Systems</h2>
            <p className="WS-slider-subtitle">Performance-driven architecture for modern digital experiences.</p>
          </div>

          <div className="WS-carousel">
            <div className="WS-carousel-viewport">
              {[
                {
                  title: "Conversion Architecture",
                  desc: "UX systems designed to guide decisions and reduce friction.",
                  meta: "High Conv Design",
                  icon: <MousePointerClick size={42} />
                },
                {
                  title: "Performance Optimization",
                  desc: "Fast, scalable builds optimized for traffic and load.",
                  meta: "Sub-1s Speeds",
                  icon: <Gauge size={42} />
                },
                {
                  title: "Revenue Alignment",
                  desc: "Web systems built to support funnels, demos, and sales flows.",
                  meta: "Sale First",
                  icon: <Layers size={42} />
                }
              ].map((slide, i) => (
                <div key={i} className={`WS-carousel-slide ${currentFeature === i ? "is-active" : ""}`}>
                  <div className="WS-slide-inner">
                    <div className="WS-slide-icon">{slide.icon}</div>
                    <h3 className="WS-slide-heading">{slide.title}</h3>
                    <p className="WS-slide-text">{slide.desc}</p>
                    <div className="WS-slide-meta">{slide.meta}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="WS-carousel-navigation">
              <button className="WS-nav-arrow" onClick={prevFeature}><ChevronLeft size={28} /></button>
              <div className="WS-nav-pagination">
                {[0, 1, 2].map(i => (
                  <button key={i} className={`WS-page-dot ${currentFeature === i ? "is-active" : ""}`} onClick={() => goToFeature(i)} />
                ))}
              </div>
              <button className="WS-nav-arrow" onClick={nextFeature}><ChevronRight size={28} /></button>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT THIS REPLACES */}
      <section className="WS-replaces-section">
        <div className="container">
          <div className="WS-dark-banner">
            <div className="banner-left">
              <h2 className="banner-title">What This Replaces</h2>
              <p className="banner-desc">Escaping the bloat of traditional web development.</p>
            </div>
            <div className="banner-grid">
              {[
                { title: "Design‑only websites", icon: <Monitor /> },
                { title: "Slow, bloated builds", icon: <Zap /> },
                { title: "Disconnected landing pages", icon: <Layout /> },
                { title: "Unmeasurable web traffic", icon: <Search /> }
              ].map((item, i) => (
                <div key={i} className="banner-item">
                  <div className="banner-icon-box">{item.icon}</div>
                  <span className="banner-label">{item.title}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* WHAT WE BUILD */}
      <section className="WS-capabilities-section">
        <div className="container">
          <div className="WS-section-header centered">
            <span className="WS-section-badge">Service Scope</span>
            <h2 className="WS-section-title">What We Build</h2>
            <p className="WS-section-subtitle">Scalable infrastructure designed for conversion and speed.</p>
          </div>
          <div className="WS-capabilities-grid">
            {[
              {
                title: "Conversion Pages",
                desc: "Conversion‑focused landing pages built to turn traffic into leads and revenue instantly.",
                icon: <MousePointerClick size={24} color="white" />,
                colorClass: "azure"
              },
              {
                title: "Full Rebuilds",
                desc: "End-to-end website rebuilds focused on speed, SEO, and enterprise-grade performance.",
                icon: <Globe size={24} color="white" />,
                colorClass: "blue"
              },
              {
                title: "UX Architecture",
                desc: "CRO‑driven UX architecture designed to guide users through complex decision journeys.",
                icon: <Layout size={24} color="white" />,
                colorClass: "slate"
              },
              {
                title: "Optimization Systems",
                desc: "Performance optimization systems that ensure zero downtime and lightning-fast loads.",
                icon: <Gauge size={24} color="white" />,
                colorClass: "azure"
              }
            ].map((cap, i) => (
              <div key={i} className="WS-capability-card">
                <div className="card-top">
                  <div className={`WS-capability-icon ${cap.colorClass}`}>
                    {cap.icon}
                  </div>
                  <h3 className="WS-capability-title">{cap.title}</h3>
                </div>
                <p className="WS-capability-desc">{cap.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* IDEAL FOR */}
      <section className="WS-ideal-section">
        <div className="container">
          <div className="WS-section-header centered">
            <h2 className="WS-section-title">Ideal For</h2>
            <p className="WS-section-subtitle">Who benchmarks their results against their web infrastructure.</p>
          </div>
          <div className="WS-ideal-grid">
            {[
              {
                title: "B2B Companies",
                desc: "Service companies needing high-intent leads and professional digital presence.",
                icon: <Building2 />
              },
              {
                title: "SaaS Operators",
                desc: "Platform operators requiring fast, conversions-driven websites for scaling demos.",
                icon: <Smartphone />
              },
              {
                title: "Growth Teams",
                desc: "Teams running paid traffic who demand measurable outcomes from every visit.",
                icon: <Users2 />
              }
            ].map((item, i) => (
              <div className="WS-ideal-card" key={i}>
                <div className="ideal-icon-circle">{item.icon}</div>
                <h4 className="ideal-title">{item.title}</h4>
                <p className="ideal-desc">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ENGAGEMENT MODEL */}
      <section className="WS-engagement-section">
        <div className="container">
          <h2 className="WS-engagement-heading">Engagement Model</h2>
          <div className="WS-steps-container">
            {[
              { title: "UX Audit", desc: "Full conversion and performance audit." },
              { title: "Preview Access", desc: "Controlled preview of architecture logic." },
              { title: "Build & Deploy", desc: "Custom development and production rollout." },
              { title: "Optimization", desc: "Ongoing support and performance tracking." }
            ].map((step, i) => (
              <div className="WS-step" key={i}>
                <div className="step-number-container">
                  <div className="WS-step-number">{i + 1}</div>
                  {i < 3 && <div className="WS-step-connector"></div>}
                </div>
                <h4 className="WS-step-h4">{step.title}</h4>
                <p className="WS-step-p">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <TrustStatement />

      <div className="LD-divider"></div>
      <ChatbotWidget />
      <Call />
      <Footer />
    </div>
  );
}
